import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { authFetch, api } from "../utils/api";
import { compressImage } from "../utils/imageCompress";

// Photo de profil refusée par un modérateur — popup distinct du blocage "compte non vérifié"
// (VerificationGateModal) : celui-ci ne peut apparaître que pour un compte DÉJÀ vérifié, jamais
// en même temps que la vérification d'identité (voir avatar_rejected, migrate.ts côté serveur).
// Monté une seule fois à la racine (voir App.tsx), lit directement `user` depuis le contexte —
// pas besoin d'un listener comme VerificationGateModal, c'est un simple champ du profil déjà
// chargé, pas une réaction à une requête bloquée.
export default function AvatarRejectedModal() {
  const { user, setUser } = useApp();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const open = !!user?.avatar_rejected && !dismissed;
  if (!open) return null;

  const pickFile = async (file: File) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", await compressImage(file, 1200, 0.9));
      const res = await authFetch("/uploads/avatar", { method: "POST", body: fd });
      const data = await res.json();
      if (data?.url) setPreview(data.url);
      else alert("Erreur lors de l'envoi de la photo");
    } catch {
      alert("Erreur lors de l'envoi de la photo");
    } finally {
      setUploading(false);
    }
  };

  const validate = () => {
    if (!preview || submitting) return;
    setSubmitting(true);
    api.put("/users/me", { avatar_url: preview })
      .then(() => {
        setUser({ ...user, avatar_url: preview, avatar_rejected: false });
        setPreview(null);
      })
      .catch(() => alert("Erreur de connexion"))
      .finally(() => setSubmitting(false));
  };

  return ReactDOM.createPortal(
    <div style={{ position: "fixed", inset: 0, backgroundColor: "white", zIndex: 4000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, overflowY: "auto" }}>
      <div style={{ width: "100%", maxWidth: 400, textAlign: "center" }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", backgroundColor: "#FDEAEA", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#C0392B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><line x1="4" y1="4" x2="20" y2="20"/></svg>
        </div>

        <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 8 }}>Ta photo de profil a été refusée</div>
        <div style={{ fontSize: 14, color: "#666", lineHeight: 1.6, marginBottom: 24 }}>
          Un membre de l'équipe a jugé ta photo actuelle non conforme. Choisis-en une autre pour continuer à utiliser OLTODO.
        </div>

        <label style={{ display: "block", width: 160, height: 160, borderRadius: "50%", backgroundColor: "#F5F5F5", margin: "0 auto 20px", cursor: "pointer", overflow: "hidden", position: "relative" }}>
          <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; if (f) pickFile(f); e.target.value = ""; }} />
          {preview ? (
            <img src={preview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, color: "#bbb" }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
              <span style={{ fontSize: 12.5, fontWeight: 600 }}>{uploading ? "Envoi…" : "Ajouter une photo"}</span>
            </div>
          )}
        </label>

        <div style={{ fontSize: 12, color: "#999", lineHeight: 1.6, marginBottom: 6 }}>
          Toute photo inappropriée (fausse identité, contenu dénudé...) entraîne la suspension du compte.
        </div>
        <button onClick={() => navigate("/help")} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: "#0a0a0a", fontSize: 13, fontWeight: 700, textDecoration: "underline", marginBottom: 28 }}>
          Aide
        </button>

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => setDismissed(true)} style={{ flex: 1, padding: "13px", borderRadius: 12, border: "1.5px solid #E0E0E0", backgroundColor: "white", color: "#666", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
            Plus tard
          </button>
          <button onClick={validate} disabled={!preview || submitting} style={{ flex: 1, padding: "13px", borderRadius: 12, border: "none", backgroundColor: preview ? "#0a0a0a" : "#E0E0E0", color: "white", fontSize: 14, fontWeight: 600, cursor: preview ? "pointer" : "default" }}>
            {submitting ? "Envoi…" : "Valider"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
